import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useUserGroupApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

// Formdan gelen yükü backend sözleşmesine normalize eder. Dışa açık: kritik davranışı
// (statik grupta kriterin DÜŞMESİ) sayfayı mount etmeden sınayabilmek için.
export const normalizeUserGroupPayload = (formData) => ({
  ...formData,
  groupType: Number(formData.groupType) === 1 ? 1 : 0,
  criteria: Number(formData.groupType) === 1 ? (formData.criteria || []) : [],
});

export default function UserGroupsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);
  // Statik → dinamik dönüşümünde elle üyelikler SİLİNİR. Kullanıcıya kaç kişinin
  // düşeceğini söylemeden kaydetmek veri kaybını sessizce yapmak olurdu.
  const [convertConfirm, setConvertConfirm] = useState({ isOpen: false, formData: null, count: 0 });
  const [converting, setConverting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, create, update, _delete } = useUserGroupApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  // GenericFormDrawer gizli alanları göndermeden TEMİZLEMEZ: kullanıcı Dinamik seçip
  // kriter ekledikten sonra Statik'e dönerse `criteria` dizisi formda kalır ve backend
  // haklı olarak `criteriaNotAllowedForStatic` ile reddeder — kullanıcı da artık
  // göremediği kriterler yüzünden hata alır. Yükü burada normalize ediyoruz.
  const kaydet = useCallback(async (formData) => {
    const yuk = normalizeUserGroupPayload(formData);
    const success = selectedRow
      ? await update(selectedRow.id, yuk)
      : await create(yuk);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleSave = useCallback(async (formData) => {
    const statiktenDinamige = selectedRow
      && Number(selectedRow.groupType) !== 1
      && Number(formData.groupType) === 1;

    if (statiktenDinamige && Number(selectedRow.memberCount) > 0) {
      setConvertConfirm({ isOpen: true, formData, count: Number(selectedRow.memberCount) });
      // Çekmece açık kalır: kullanıcı vazgeçerse düzenlemesi kaybolmaz.
      return false;
    }

    return kaydet(formData);
  }, [selectedRow, kaydet]);

  const handleConvertConfirm = useCallback(async () => {
    setConverting(true);
    await kaydet(convertConfirm.formData);
    setConvertConfirm({ isOpen: false, formData: null, count: 0 });
    setConverting(false);
  }, [kaydet, convertConfirm.formData]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('userGroup.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const handleRowClick = useCallback((row) => {
    history.push(`/user-groups/${row.id}`);
  }, [history]);

  const actionOptions = useMemo(() => {
    if (!canWrite('EM')) return [];
    return [
      { type: 'details', label: t('userGroup.details'), onClick: (row) => history.push(`/user-groups/${row.id}`) },
      { type: 'edit', label: t('userGroup.edit'), onClick: handleEdit },
      { type: 'divider' },
      {
        type: 'delete', label: t('userGroup.delete'),
        onClick: handleDelete,
        disabled: (row) => row.isAllUsers,
      },
    ];
  }, [t, canWrite, handleEdit, handleDelete, history]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('userGroup.edit') : t('userGroup.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={convertConfirm.isOpen}
        title={t('userGroup.convertToDynamicTitle')}
        message={t('userGroup.convertToDynamicMessage', { count: convertConfirm.count })}
        onConfirm={handleConvertConfirm}
        onCancel={() => setConvertConfirm({ isOpen: false, formData: null, count: 0 })}
        loading={converting}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('userGroup.deleteTitle')}
        message={t('userGroup.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('userGroup.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('EM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('EM') ? handleRowsDelete : undefined}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, filterList,
        handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
